import {
  useModalContext,
  WSButton,
  WSCheckboxToggle,
  WSFlexBox,
  WSFormOld,
  WSIcon,
  WSInputMaskOld,
  WSModal,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { VerificationStatus } from "@wingspanhq/payments/dist/interfaces";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { ProgressBar } from "../../../MemberOnboarding/components/ProgressBar/ProgressBar";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYMENTS_STATUS } from "../../../query/payments/keys";
import { usePaymentsStatusQuery } from "../../../query/payments/queries";
import {
  useActivities,
  useMemberProfile,
  useUserProfile
} from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { stringToUndefinedIfEmpty } from "../../../shared/utils/stringToUndefinedIfEmpty";
import { openIntercom } from "../../../utils/intercom";
import { WSServiceError } from "../../../utils/serviceHelper";
import {
  companyNameValidator,
  validatorFirstName,
  validatorLastName,
  validatorSSN,
  validatorTaxId
} from "../../../utils/validators";
import { W9ConcentForm } from "../W9ConcentForm/W9ConcentForm";
import styles from "./styles.module.scss";
import { QUERY_NOTIFICATIONS_NOTIFICATIONS } from "../../../query/notifications/keys";
import { getSsnInputProps } from "../../../shared/utils/getSsnInputProps";

export const TIN_VERIFICATION_MODAL = "TIN_VERIFICATION_MODAL";
export const WHAT_IS_TIN_MODAL = "WHAT_IS_TIN_MODAL";

type W9VerificationFormValues = {
  iHaveEIN: boolean;
  firstName: string;
  lastName: string;
  businessName: string;
  ssn: string;
  ein: string;
};

export const TINVerificationModal: React.FC = () => {
  return (
    <>
      <WSModal name={TIN_VERIFICATION_MODAL} size="S" blockClose>
        {props => <TINVerificationModalInner {...props} />}
      </WSModal>
      <WSModal
        name={WHAT_IS_TIN_MODAL}
        size="S"
        title="What is a Taxpayer Identification Number?"
      >
        <WSText mb="M">
          A Taxpayer Identification Number (TIN) is an Employer Identification
          number (EIN) or a Social Security Number (SSN). Wingspan collects this
          information to facilitate client’s tax reporting requirements.
        </WSText>
        <WSText mb="M">Here is what the IRS</WSText>
        <WSText mb="XL">
          Have a question?{" "}
          <WSButton.Link onClick={openIntercom}>Contact support</WSButton.Link>
        </WSText>
      </WSModal>
    </>
  );
};

type ModalProps = { onContinue?: () => void };

export const TINVerificationModalInner: React.FC<ModalProps> = ({
  onContinue
}) => {
  const userId = useUserId();
  const paymentStatusQuery = usePaymentsStatusQuery();
  const [isTimedOut, setIsTimedOut] = useState(false);
  const { openModal, closeModal } = useModalContext();
  const userQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const activityQuery = useActivities(userId);

  const [runVerification, runVerificationMeta] = useWSMutation(
    async () => {
      await paymentsService.service.update(userId, {
        verifications: {
          taxDocumentation: VerificationStatus.Verified
        }
      });
    },
    { dependencies: [QUERY_PAYMENTS_STATUS, QUERY_NOTIFICATIONS_NOTIFICATIONS] }
  );

  useEffect(() => {
    // Once we got current tax documentation status and it's None, we can try to run verification
    if (
      paymentStatusQuery.data?.verifications.taxDocumentation ===
      VerificationStatus.None
    ) {
      runVerification();
    }
  }, [paymentStatusQuery.data?.verifications.taxDocumentation]);

  useEffect(() => {
    if (
      paymentStatusQuery.data?.verifications.taxDocumentation ===
      VerificationStatus.Verified
    ) {
      const timeout = setTimeout(() => {
        closeModal(TIN_VERIFICATION_MODAL);
        onContinue?.();
      }, 2500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [paymentStatusQuery.data?.verifications.taxDocumentation]);

  const [updateUserData, updateUserDataMeta] = useWSMutation<
    any,
    WSServiceError,
    W9VerificationFormValues
  >(
    async values => {
      await usersService.user.update(userId, {
        profile: {
          firstName: values.firstName,
          lastName: values.lastName
        }
      });

      if (values.iHaveEIN) {
        await usersService.member.update(userId, {
          memberId: userId,
          profile: {
            company: {
              name: stringToUndefinedIfEmpty(values.businessName),
              taxId: values.ein,
              legalBusinessName: values.businessName
            }
          }
        });
      } else {
        await usersService.member.update(userId, {
          memberId: userId,
          profile: {
            ssn: values.ssn
          }
        });
      }
    },
    {
      onSuccess: () => {
        runVerification();
      }
    }
  );

  return paymentStatusQuery.data?.verifications.taxDocumentation ===
    VerificationStatus.Failed &&
    !updateUserDataMeta.isLoading &&
    !runVerificationMeta.isLoading ? (
    <WSQueries
      queries={{
        userQuery,
        memberQuery,
        activityQuery
      }}
    >
      {({
        userQuery: { data: user },
        memberQuery: { data: member },
        activityQuery: { data: activity }
      }) => {
        return activity.events.w9FormAcknowledged ? (
          <WSFormOld<W9VerificationFormValues>
            defaultValues={{
              iHaveEIN: !!member.profile.company?.taxId,
              firstName: user.profile.firstName,
              lastName: user.profile.lastName,
              businessName: member.profile.company?.name
            }}
            validationSchema={Yup.object().shape({
              firstName: validatorFirstName.required("Required"),
              lastName: validatorLastName.required("Required"),
              businessName: Yup.string().when("iHaveEIN", {
                is: true,
                then: companyNameValidator.required("Required")
              }),
              ein: Yup.string().when("iHaveEIN", {
                is: true,
                then: validatorTaxId.required("Required")
              }),
              ssn: Yup.string().when("iHaveEIN", {
                is: true,
                then: Yup.string(),
                otherwise: validatorSSN.required("Required")
              })
            })}
            onSubmit={updateUserData}
          >
            <WSText weight="medium" mb="XL">
              We could not match your Taxpayer Identification Number and name
              with the IRS.
            </WSText>

            <WSText mb="M">Here are some suggestions:</WSText>
            <WSText mb="XL" className={styles.list}>
              <ol>
                <li>
                  If you have an EIN, confirm your business name, legal name and
                  EIN matches what appears your business's EIN confirmation
                  document (either IRS form 147c or IRS form CP-575).
                </li>
                <li>
                  If you don’t yet have an EIN and want one, apply here with the
                  IRS.
                </li>
                <li>
                  Otherwise,{" "}
                  <WSButton.Link onClick={openIntercom}>
                    contact Wingspan support
                  </WSButton.Link>
                  .
                </li>
              </ol>
            </WSText>

            <WSFormOld.Field
              name="iHaveEIN"
              component={WSCheckboxToggle}
              componentProps={{
                label: "I have an Employer Identification Number"
              }}
              mb="XL"
            />

            <WSFlexBox wrap="nowrap" mb="XL">
              <WSFormOld.Field
                name="firstName"
                label="Legal first name"
                component={WSTextInput}
                mr="XL"
              />

              <WSFormOld.Field
                name="lastName"
                label="Legal last name"
                component={WSTextInput}
              />
            </WSFlexBox>

            <WSFormOld.Value name="iHaveEIN">
              {iHaveEIN => (
                <>
                  <WSFormOld.Field
                    hidden={iHaveEIN}
                    name="ssn"
                    label="SSN"
                    component={WSInputMaskOld}
                    componentProps={getSsnInputProps()}
                    mb="XL"
                  />

                  <WSFormOld.Field
                    hidden={!iHaveEIN}
                    name="businessName"
                    label="Business name"
                    component={WSTextInput}
                    mb="XL"
                  />

                  <WSFormOld.Field
                    hidden={!iHaveEIN}
                    name="ein"
                    label="EIN"
                    component={WSInputMaskOld}
                    componentProps={{
                      mask: "99-9999999",
                      unmask: true,
                      fsExclude: true
                    }}
                    mb="XL"
                  />
                </>
              )}
            </WSFormOld.Value>

            <WSErrorMessage
              mb="XL"
              contextKey="PaymentsSetup"
              error={updateUserDataMeta.error}
            />

            <WSButton
              mt="2XL"
              fullWidth
              loading={updateUserDataMeta.isLoading}
              name="next"
            >
              Continue
            </WSButton>

            <WSFlexBox.Center mt="XL">
              <WSButton.Link
                type="button"
                onClick={() => {
                  openModal(WHAT_IS_TIN_MODAL);
                }}
              >
                What is a TIN?
              </WSButton.Link>
            </WSFlexBox.Center>
          </WSFormOld>
        ) : (
          <>
            <WSText.Heading5 mb="XL">
              Certify your W-9 is accurate
            </WSText.Heading5>
            <W9ConcentForm onSubmit={runVerification} />
          </>
        );
      }}
    </WSQueries>
  ) : paymentStatusQuery.data?.verifications.taxDocumentation ===
    VerificationStatus.Verified ? (
    <>
      <WSFlexBox.CenterY wrap="nowrap" mb="XL" justify="space-between">
        <WSText weight="medium">Taxpayer Identification Number verified</WSText>
        <WSIcon block name="check" color="green500" />
      </WSFlexBox.CenterY>

      <ProgressBar mb="XL" ms={20000} showCompleted />

      <WSText>
        We successfully verified your TIN with the IRS. You will be redirected
        in a moment.
      </WSText>
    </>
  ) : isTimedOut ? (
    <>
      <WSText weight="medium" mb="XL">
        We are still waiting verify your Taxpayer Identification Number with the
        IRS.
      </WSText>

      <WSText mb="XL">
        We have not heard back from the IRS regarding your TIN. We will attempt
        to verify it again in 24 hours and will reach out if there are any
        issues.
      </WSText>
      <WSButton
        fullWidth
        onClick={() => {
          closeModal(TIN_VERIFICATION_MODAL);
          onContinue?.();
        }}
      >
        Continue
      </WSButton>
    </>
  ) : (
    <TINProgress
      onTimeout={() => {
        setIsTimedOut(true);
        paymentStatusQuery.refetch();
      }}
      timeoutMs={20000}
    />
  );
};

type TINProgressProps = {
  timeoutMs: number;
  onTimeout: () => void;
};

const TINProgress: React.FC<TINProgressProps> = ({ timeoutMs, onTimeout }) => {
  usePaymentsStatusQuery({ refetchInterval: 5000 });
  useEffect(() => {
    const timeout = setTimeout(onTimeout, timeoutMs);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <>
      <WSText weight="medium" mb="XL">
        Verifying your Taxpayer Identification Number
      </WSText>

      <ProgressBar mb="XL" ms={timeoutMs} />

      <WSText>
        We are confirming your TIN with the IRS. This can take up to 10 seconds.
      </WSText>
    </>
  );
};
